import type { AddLayerWindowProps } from "@/overlay/components/AddLayerWindow";
import type { AddParameterWindowProps } from "@/overlay/components/AddParameterWindow/AddParameterWindow";
import type { ThresholdManagerWindowProps } from "@/overlay/components/CommonParts/Threshold/ThresholdManagerWindow";
import type { CustomPopupProps } from "@/overlay/components/CustomPopupWindow/popup";
import type { PortfolioManagerWindowProps } from "@/overlay/components/PortfolioManagerWindow";
import type { RenameDashboardWindowProps } from "@/overlay/components/RenameDashboardWindow";
import type { LocationResult } from "@mm/geocoder.meteomatics.com";
import type { GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import type { DateTime, Duration } from "luxon";
import type { ConfirmationWindowProps } from "../ConfirmationWindow";
import type { InputConfirmationWindowProps } from "../InputConfirmationWindow";
import type { PoiManagerWindowProps } from "../PoiManagerWindow";
import type { WhatsNewWindowProps } from "../WhatsNewWindow";
import type { ExploreWindowProps } from "../WhatsNewWindow/ExploreWindow";
import type { WindowInstanceId } from "./DesktopContext";
/**
 * Window identifiers may be arbitrary strings and change at runtime. (This is useful if a window can
 * be opend an arbitrary number of times or if windows are dynamically generated at runtime.)
 *
 * These are just some type-safe wrappers for the strings available in the metx default overlay.
 */
export enum WindowKind {
  DatetimeSelection = "DatetimeSelection",
  DatetimeSlider = "DatetimeSlider",
  DatetimeLabel = "DatetimeLabel",
  Account = "Account",
  LayoutSettings = "LayoutSettings",
  LayerStack = "LayerStack",
  AddLayer = "AddLayer",
  AddParameter = "AddParameter",
  AddDashboard = "AddDashboard",
  RenameDashboard = "RenameDashboard",
  About = "About",
  Developer = "Developer",
  PopupPlot = "PopupPlot",
  LocationSearch = "LocationSearch",
  DeleteConfirmation = "DeleteConfirmation",
  ConfirmationWindow = "ConfirmationWindow",
  InputConfirmationWindow = "InputConfirmationWindow",
  FileWindow = "FileWindow",
  PoiManagerWindow = "PoiManagerWindow",
  PortfolioManagerWindow = "PortfolioManagerWindow",
  ThresholdManagerWindow = "ThresholdManagerWindow",
  CustomPopup = "CustomPopup",
  Shortcuts = "Shortcuts",
  WhatsNewWindow = "WhatsNewWindow",
  ExploreWindow = "ExploreWindow",
  AiChatWindow = "AiChatWindow",
  LocationSearchWindow = "LocationSearchWindow",
  CalendarControlWindow = "CalendarControlWindow",
}

/**
 * Conditional type to define custom prop object that could be passed to windows
 * using "setWindowCustomProps" action.
 *
 * To enable customProps to a new component, define the prop type for each window here.
 * @example
 *     K extends WindowKind.<NewWindowKind> ? NewWindowCustomPropType :
 *     K extends WindowKind.<NewWindowKind2> ? NewWindowCustomPropType2 :
 *     EmptyObject
 *
 */
interface WindowPropsMapping {
  [WindowKind.AddLayer]: AddLayerWindowProps;
  [WindowKind.AddParameter]: AddParameterWindowProps;
  [WindowKind.PortfolioManagerWindow]: PortfolioManagerWindowProps;
  [WindowKind.ConfirmationWindow]: ConfirmationWindowProps;
  [WindowKind.InputConfirmationWindow]: InputConfirmationWindowProps;
  [WindowKind.RenameDashboard]: RenameDashboardWindowProps;
  [WindowKind.PoiManagerWindow]: PoiManagerWindowProps;
  [WindowKind.ThresholdManagerWindow]: ThresholdManagerWindowProps;
  [WindowKind.CustomPopup]: CustomPopupProps;
  [WindowKind.WhatsNewWindow]: WhatsNewWindowProps;
  [WindowKind.ExploreWindow]: ExploreWindowProps;
  [WindowKind.LocationSearchWindow]: {
    onSelect?: (loc: LocationResult, options?: { isCoordInput?: boolean }) => void;
    onCloseOverlay?: () => void;
    sourceCellName?: string;
    defaultTab?: "location" | "station";
    showStationSearch?: boolean;
    isAdvancedStationView?: boolean;
  };
  [WindowKind.CalendarControlWindow]: {
    dateTimeFrom: string | null;
    dateTimeTo: string | null;
    timezone: GuiTimeZone;
    resolution: string | null;
    onSubmitDatetimeRange: (from: DateTime, to: DateTime, resolution: Duration) => void;
    onTimeLockChange: (locked: boolean) => void;
    onCloseOverlay?: () => void;
    timeLock: boolean | null;
    showInterval?: boolean;
    minDate?: DateTime;
    maxDate?: DateTime;
    defaultRange: [DateTime, DateTime];
    sourceCellName?: string;
  };
}

export type WindowCustomProps<K extends WindowKind> = K extends keyof WindowPropsMapping
  ? Partial<WindowPropsMapping[K]>
  : Partial<EmptyObject>;

type EmptyObject = {
  [property: string]: never;
};

export function instanceId(kind: WindowKind, refCount = 0): WindowInstanceId {
  return [kind, refCount].join(":");
}
